package one.devsbox.www.ui.apps.wheel.edit

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import one.devsbox.shared.a.App
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.provider.LocalMeProvider
import one.devsbox.www.provider.LocalPreferencesProvider
import one.devsbox.www.provider.MeProvider
import one.devsbox.www.provider.PreferencesProvider
import one.devsbox.www.tools.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.args
import tk.mallumo.compose.navigation.viewmodel.viewModel

import one.devsbox.www.provider.LocalNavigationCallbackProvider
import one.devsbox.www.provider.NavigationCallbackProvider

class AppWheelECtx private constructor(
    val vm: AppWheelEVM,
    val nav: Navigation,
    val args: ArgsIdWithParent,
    val navCallback: NavigationCallbackProvider,
    val me: MeProvider,
    val prefs: PreferencesProvider,
    val colorPrimaryContainer: Color,
    val colorSecondaryContainer: Color,
    val colorErrorContainer: Color
) {
    companion object {
        @Composable
        fun get(): AppWheelECtx {
            val nav = LocalNavigation.current
            val args = LocalNavigationArgs.current.args<ArgsIdWithParent>()
            val vm = nav.viewModel(AppWheelEVM::class)
            val me = LocalMeProvider.current
            val prefs = LocalPreferencesProvider.current
            val navCallback = LocalNavigationCallbackProvider.current
            val colorPrimary = MaterialTheme.colorScheme.primaryContainer
            val colorSecondary = MaterialTheme.colorScheme.secondaryContainer
            val colorError = MaterialTheme.colorScheme.errorContainer
            return remember {
                AppWheelECtx(
                    vm, nav, args, navCallback, me, prefs,
                    colorPrimary, colorSecondary, colorError
                ).apply {
                    vm.setup(this)
                }
            }
        }
    }

    val action get() = vm.action

    class Editor(
        val appInstance: SharedIO.AppInstance,
        val wheel: App.Wheel = appInstance.data as App.Wheel
    ) {
        val name = EFieldTextString(
            property = appInstance::name,
            condition = { it.trim().takeIf { it.isNotEmpty() } }
        )

        val orientation = EFieldRegularInt(
            property = wheel.content.theme::orientation
        )

        val themeBgColor = EFieldColorAsLong(
            property = wheel.content.theme::backgroundColor
        )

        val themeItemColorBg = EFieldColorAsLong(
            property = wheel.content.theme.items::surface
        )

        val themeItemColorFg = EFieldColorAsLong(
            property = wheel.content.theme.items::onSurface
        )

        val themeTriangleColor = EFieldColorAsLong(
            property = wheel.content.theme::triangleColor
        )

        val wheelCenterColor = EFieldColorAsLong(
            property = wheel.content.wheel.color::onSurface
        )

        val wheelColor = EFieldColorAsLong(
            property = wheel.content.wheel.color::surface
        )

        val themeItemFontSize = EFieldDualIntAsFloat(
            property = wheel.content.theme::itemsFontSize
        )

        val themeItemNameY = EFieldDualIntAsFloat(
            property = wheel.content.theme::itemNameY
        )

        val themeItemIconsScale = EFieldDualIntAsFloat(
            property = wheel.content.theme::iconsScale
        )

        val themeItemIconsY = EFieldDualIntAsFloat(
            property = wheel.content.theme::iconsY
        )

        val wheelScale = EFieldDualIntAsFloat(
            property = wheel.content.wheel::scale
        )

        val wheelX = EFieldDualIntAsFloat(
            property = wheel.content.wheel::x
        )

        val wheelY = EFieldDualIntAsFloat(
            property = wheel.content.wheel::y
        )

        val wheelScaleCenter = EFieldDualIntAsFloat(
            property = wheel.content.wheel::scaleCenter
        )

        val themeBgApp = EFieldRegularLong(
            property = wheel.content.theme::backgroundApp
        )

        val themeBgStartup = EFieldRegularLong(
            property = wheel.content.theme::backgroundStartup
        )

        val timerSplash = EFieldTextMsAsSec(
            property = wheel.content.timer::splash
        )

        val timerGame = EFieldTextMsAsSec(
            property = wheel.content.timer::game
        )

        val timerResult = EFieldTextMsAsSec(
            property = wheel.content.timer::result
        )

        val items = EFieldList<App.Wheel.Item, ItemEditor>(
            items = wheel.content.items,
            onBindEntry = { ItemEditor(it) }
        )

        class ItemEditor(
            override val data: App.Wheel.Item,
            override val key: String = data.idGroup.toString()
        ) : EFieldList.Entry<App.Wheel.Item> {

            val name = EFieldTextString(
                property = data::name,
                condition = { it.trim().takeIf { it.isNotEmpty() } }
            )

            val colorBg = EFieldColorAsLong(
                property = data.color::surface
            )

            val colorFg = EFieldColorAsLong(
                property = data.color::onSurface
            )

            val fontSize = EFieldDualIntAsFloat(
                property = data::fontSize
            )

            val iconWebp = EFieldRegularLong(
                property = data.icon::webp
            )

            val rewardVisual = EFieldRegularLong(
                property = data.reward::visual
            )

            val position = EFieldRegularInt(
                property = data::position
            )

            val count = EFieldTextInt(
                property = data::count,
                condition = { it.trim().toIntOrNull()?.takeIf { it > 0 } }
            )

            override fun onPositionChanged(newPosition: Int) {
                position.set(newPosition)
            }
        }
    }
}
